import {useAtom, useAtomValue} from 'jotai';
import {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {Button, IconButton} from '@/components/Button';
import toast from '@/components/HotToast';
import {InfoIconComponent} from '@/components/Icons';
import {Typography} from '@/components/Typography';
import {useGA4WithParams} from '@/hooks/tracking';
import {AnalysisItem} from '@/map/@types';
import {anaysisListAtom, compareAreaItemsAtom} from '@/map/atom';
import {AreaOverlayType} from '@/map/enums';
import {panToBoundCircle, panToBoundsPolygon} from '@/map/modules';
import {getKm2} from '@/map/utils';
import {
  Divider,
  Flexbox,
  FlexCenter,
  FlexFullGrow,
  IconBigClose,
  IconClose,
  PaddingBox,
} from '@/styled-components';
import {palette} from '@/styles';
import IconRotate from './assets/rotate-right.svg';
import {InfoModal} from './InfoModal';
import {ListItem} from './ListItem';
import {CompareReport} from './Report';
import {Chip, Chips, FocusIcon, Footer, StatusBar} from './StyledComponents';

interface IProps {
  map: naver.maps.Map;
  onClose?(): void;
}

const MAX_COUNT = 3;

export const CompareArea = ({map, onClose}: IProps) => {
  const analysisList = useAtomValue(anaysisListAtom);

  const [selected, setSelected] = useAtom(compareAreaItemsAtom);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [startCompare, setStartCompare] = useState<boolean>(false);

  const tracking = useGA4WithParams();

  const list = useMemo(() => {
    const filtered = analysisList.filter(
      (i) => i.id && i.type !== AreaOverlayType.EMD
    );

    return filtered.reverse();
  }, [analysisList]);

  const onFocusArea = (item: AnalysisItem) => {
    const margin = {left: 500, right: 350};

    if (item.polygon?.paths) {
      panToBoundsPolygon(item.polygon.paths, map, margin);
    } else if (item.circle?.center && item.circle.radius) {
      panToBoundCircle(item.circle as any, map, margin);
    }
  };

  useEffect(() => {
    const validSelected = selected.filter((id) =>
      list.some((item) => item.id === id)
    );

    if (validSelected.length !== selected.length) {
      setSelected(validSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const compareList = useMemo(() => {
    return selected
      .map((id) => list.find((i) => i.id === id))
      .filter((x) => x) as AnalysisItem[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const onRemoveItem = (id: string) => {
    setSelected((prev) => {
      return prev.filter((i) => i !== id);
    });
  };

  const onChange = (id: string, checked: boolean) => {
    if (checked) {
      if (selected.length >= MAX_COUNT) {
        toast.error(`비교할 상권은 ${MAX_COUNT}개까지 선택할 수 있습니다.`);
        return;
      }

      const item = list.find((v) => String(v.id) === id);

      if (item) {
        const limit =
          item.type === AreaOverlayType.POLYGON
            ? getKm2(item.polygon?.m2 as number) > 3
            : (item.circle?.radius as number) > 1000;

        if (limit) {
          toast.error(
            '반경 1km 이하 및 다각형 면적 3km² 이하의 상권에서 제공 중인 서비스 입니다.'
          );
          return;
        }
      }

      setSelected((prev) => [...prev, id]);
    } else {
      onRemoveItem(id);
    }
  };

  const onStartCompare = () => {
    setStartCompare(true);
    tracking('analysis_compare_use');
  };

  if (startCompare) {
    return (
      <Root style={{height: 'auto'}}>
        <TitleBar>
          <Typography variant="bold18" fontWeight="700">
            상권 비교
          </Typography>
          <InfoIconComponent onClick={() => setOpenInfo(true)} />
          <FlexFullGrow />
          <IconBigClose onClick={onClose} />
        </TitleBar>
        <CompareReport list={compareList} />
        <Divider />
        <StatusBar>
          <Chips style={{paddingBottom: 12}}>
            {compareList.map(
              (i, idx) =>
                !!i && (
                  <Chip key={i.id}>
                    비교 상권 {idx + 1}
                    <FocusIcon onClick={() => onFocusArea(i)} />
                  </Chip>
                )
            )}
          </Chips>
        </StatusBar>
        {openInfo && <InfoModal onClose={() => setOpenInfo(false)} />}
      </Root>
    );
  }

  return (
    <Root>
      <TitleBar>
        <Typography variant="title6" fontWeight="700">
          상권 비교
        </Typography>
        <InfoIconComponent onClick={() => setOpenInfo(true)} />
        <FlexFullGrow />
        <IconBigClose onClick={onClose} />
      </TitleBar>
      <Divider />
      <ScrollBody>
        {list && list.length > 0 ? (
          list.map((i) => (
            <ListItem
              key={`compare-item-${i.id}`}
              item={i}
              map={map}
              onChange={onChange}
            />
          ))
        ) : (
          <FlexCenter>
            <PaddingBox top={16}>
              <Typography variant="medium14">
                다각형/반경 상권이 없습니다.
              </Typography>
            </PaddingBox>
          </FlexCenter>
        )}
      </ScrollBody>
      <Divider />
      <StatusBar>
        <PaddingBox left={16} right={16}>
          <Flexbox>
            <IconButton
              variant="ghost"
              noPadding
              size="small"
              icon={IconRotate}
              onClick={() => setSelected([])}
            >
              <strong>선택 초기화</strong>
            </IconButton>
            <FlexFullGrow />
            <Typography color={palette.blue400}>{selected.length}</Typography>
            <Typography color={palette.textSecondary}>/{MAX_COUNT}</Typography>
          </Flexbox>
        </PaddingBox>
        {!!selected.length ? (
          <Chips>
            {selected.map((i, idx) => (
              <Chip key={`compare-chip-${idx}`}>
                비교 상권 {idx + 1}
                <IconClose onClick={() => onRemoveItem(i)} />
              </Chip>
            ))}
          </Chips>
        ) : (
          <PaddingBox top={15} bottom={15} left={16}>
            <Typography variant="medium12" color={palette.textSecondary}>
              비교할 상권을 2개 이상 선택해주세요.
            </Typography>
          </PaddingBox>
        )}
      </StatusBar>
      <Footer>
        <Button
          variant="fill"
          color="secondary"
          size="regular"
          fullWidth
          disabled={selected.length < 2}
          onClick={onStartCompare}
        >
          비교하기
        </Button>
      </Footer>
      {openInfo && <InfoModal onClose={() => setOpenInfo(false)} />}
    </Root>
  );
};

const Root = styled.div`
  width: 320px;
  height: 400px;
  max-height: calc(100% - 110px);
  border-radius: 8px;
  box-shadow: 0px 4px 10px 0px #00000026;
  border: 1px solid ${palette.gray300};
  background: ${palette.white};
  display: flex;
  flex-direction: column;
  font-weight: 500;
  pointer-events: auto;
`;

const TitleBar = styled(Flexbox)`
  padding: 12px 16px;
  color: ${palette.textPrimary};
  gap: 4px;
`;

const ScrollBody = styled.div`
  flex-grow: 1;
  overflow: auto;
`;
